import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/* Data                                                                */
/* ------------------------------------------------------------------ */

const PLATFORMS = [
  {
    name: "Instagram",
    audience: "25-45 year olds, visual-first, high engagement",
    bestFor: "Reels, carousel posts, Stories, property showcases",
    tips: [
      "Post Reels (15-30 seconds) for every new listing; they reach far beyond your followers",
      "Use carousel posts for property walkthroughs (5-10 image slideshow)",
      "Stories for behind-the-scenes: viewings, sold boards, team updates",
      "Use local hashtags (#BristolProperty, #LondonHomes) and location tags",
      "Post 4-5 times per week for consistent growth",
    ],
  },
  {
    name: "TikTok",
    audience: "18-35 year olds, fast-growing property content niche",
    bestFor: "Short property tours, market tips, agent personality content",
    tips: [
      "Keep videos under 30 seconds; the first 2 seconds must hook the viewer",
      "Trending audio boosts reach significantly; use popular sounds with property content",
      "Show personality: agents who speak to camera build stronger personal brands",
      "Post area guides and market commentary alongside listings",
      "TikTok rewards consistency; aim for daily posting to build momentum",
    ],
  },
  {
    name: "Facebook",
    audience: "30-65 year olds, largest UK user base, strong local groups",
    bestFor: "Longer videos, community engagement, local group marketing",
    tips: [
      "Post property videos natively (not as YouTube links) for 6x more engagement",
      "Join and contribute to local community groups (not just listing spam)",
      "Facebook Marketplace is increasingly used for property searches",
      "Boost high-performing posts to target specific demographics and locations",
      "Respond to every comment and message; Facebook rewards responsive pages",
    ],
  },
  {
    name: "LinkedIn",
    audience: "Professionals, B2B, premium property market",
    bestFor: "Industry commentary, agency brand building, premium listings",
    tips: [
      "Share market insights, industry commentary, and agency milestones",
      "Post premium property listings to reach high-net-worth buyers",
      "Use LinkedIn articles for thought leadership on property market trends",
      "Tag relevant connections and companies to extend reach",
      "Best for building your agency brand rather than direct property sales",
    ],
  },
  {
    name: "YouTube",
    audience: "All ages, search-driven, long-form content",
    bestFor: "Property tours, area guides, educational content, Shorts",
    tips: [
      "YouTube Shorts (under 60 seconds) compete directly with Reels and TikTok",
      "Longer property tours (2-5 minutes) rank in Google search results",
      "Area guide videos build long-term organic traffic",
      "Optimise titles and descriptions with keywords buyers actually search",
      "YouTube content has the longest shelf life of any social platform",
    ],
  },
]

const CONTENT_IDEAS = [
  { type: "Listing video", frequency: "Every new listing", platform: "All platforms" },
  { type: "Sold/let announcement", frequency: "Every completion", platform: "Instagram, Facebook" },
  { type: "Area guide", frequency: "Monthly", platform: "YouTube, TikTok, Instagram" },
  { type: "Market update", frequency: "Monthly", platform: "LinkedIn, Facebook" },
  { type: "Behind the scenes", frequency: "Weekly", platform: "Instagram Stories, TikTok" },
  { type: "Client testimonial", frequency: "When available", platform: "All platforms" },
  { type: "Property tip or advice", frequency: "2x per week", platform: "Instagram, TikTok" },
  { type: "Team spotlight", frequency: "Monthly", platform: "Instagram, LinkedIn" },
]

const BEST_PRACTICES = [
  {
    title: "Consistency beats perfection",
    detail:
      "Posting three decent videos per week will outperform one perfect video per month. Social algorithms reward accounts that post regularly. Batch-create content when you can, and use scheduling tools to maintain a steady cadence.",
  },
  {
    title: "Video outperforms everything",
    detail:
      "Across every platform, video content generates more reach, engagement, and enquiries than static images or text posts. Short-form video (Reels, TikTok, Shorts) is the highest-performing format in 2026. If you are not posting video, you are invisible to the algorithm.",
  },
  {
    title: "Lead with value, not sales",
    detail:
      "The 80/20 rule applies: 80% of your content should inform, entertain, or help your audience. 20% can be promotional. Area guides, market tips, and property advice build trust. Constant sales pitches drive people away.",
  },
  {
    title: "Engage, do not just broadcast",
    detail:
      "Reply to every comment. Answer DMs promptly. Engage with other local accounts. Social media is a conversation, not a billboard. The agents who treat it as a community tool consistently outperform those who treat it as an advertising channel.",
  },
  {
    title: "Track what works",
    detail:
      "Review your analytics weekly. Which posts got the most reach? Which generated enquiries? Double down on what works and stop doing what does not. Most agents post blindly without ever checking their metrics.",
  },
  {
    title: "Repurpose everything",
    detail:
      "One property video can become an Instagram Reel, a TikTok, a YouTube Short, a Facebook post, and a LinkedIn update. Repurposing multiplies your output without multiplying your effort. Tools like QuiteRight create videos that work across all platforms and aspect ratios.",
  },
]

const FAQ = [
  {
    q: "Which social media platform is best for estate agents?",
    a: "Instagram and Facebook are the most effective for most UK estate agents. Instagram Reels reach younger buyers and generate strong engagement, while Facebook has the largest UK user base and powerful local targeting. TikTok is growing rapidly, especially for agents willing to create personality-driven content. The best approach is to focus on 2-3 platforms and post consistently rather than spreading yourself too thin.",
  },
  {
    q: "How often should an estate agent post on social media?",
    a: "Aim for 4-5 posts per week across your main platforms. This should include a mix of property listings, area content, market updates, and behind-the-scenes posts. Consistency is more important than frequency; three posts every week is better than ten posts one week and none the next.",
  },
  {
    q: "Do I need to create separate content for each platform?",
    a: "Not necessarily. A 15-30 second property video can be posted to Instagram Reels, TikTok, YouTube Shorts, and Facebook with minimal adaptation. You may want to adjust captions and hashtags for each platform, but the core video content can be repurposed across all of them.",
  },
  {
    q: "How do I measure social media ROI as an estate agent?",
    a: "Track direct enquiries from social media (DMs, comments, link clicks), follower growth, and engagement rate (likes + comments + shares divided by followers). For a more complete picture, ask new vendors and buyers how they found you. Many agents are surprised by how often the answer is social media.",
  },
  {
    q: "What tools can help estate agents with social media?",
    a: "QuiteRight creates professional property videos from listing photos in under 60 seconds, perfect for Reels and TikTok. Scheduling tools like Buffer or Later help maintain posting consistency. Canva is useful for static graphics. For analytics, each platform has built-in insights that are sufficient for most agents.",
  },
]

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export default function SocialMediaEstateAgentsGuide({
  meta,
}: {
  meta: SeoMeta
}) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline:
              "Social Media for Estate Agents: The Complete Guide",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-22",
            dateModified: "2026-02-22",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Guides", path: "/guides" }, { name: "Social Media for Estate Agents" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Guide
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Social media for estate agents
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            The complete guide to social media marketing for UK estate
            agents. Platform strategies, content ideas, posting schedules,
            and practical tips to win instructions and sell properties.
          </p>
        </div>
      </section>

      <SocialShareBar />

      {/* Intro */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Why social media matters for agents
            <span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              Social media is no longer optional for estate agents. It is
              where vendors research agents before booking a valuation,
              where buyers discover properties before they appear on
              portals, and where your agency brand is built or forgotten.
            </p>
            <p>
              The agents winning the most instructions in 2026 are the
              ones showing up consistently on Instagram, TikTok, and
              Facebook with professional video content that showcases
              their properties and expertise. They are not just listing
              properties; they are building audiences that convert into
              business.
            </p>
            <p>
              This guide covers everything you need to build a social
              media strategy that actually works, from platform selection
              to content planning to measuring results.
            </p>
          </div>
        </div>
      </section>

      {/* Platform guides */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Platform-by-platform guide
            <span className="text-red">.</span>
          </h2>
          <p className="mt-2 text-sm text-slate">
            Each platform has its own strengths. Here is how to use them
            effectively.
          </p>

          <div className="mt-8 space-y-6">
            {PLATFORMS.map((platform) => (
              <ScrollReveal key={platform.name}>
                <div className="rounded-xl border border-stone bg-white p-6">
                  <h3 className="font-display text-lg font-bold text-charcoal">
                    {platform.name}
                  </h3>
                  <p className="mt-1 text-xs text-warm-grey">
                    {platform.audience}
                  </p>
                  <p className="mt-2 text-sm text-slate">
                    <strong className="text-charcoal">Best for:</strong>{" "}
                    {platform.bestFor}
                  </p>
                  <ul className="mt-3 space-y-1.5">
                    {platform.tips.map((tip) => (
                      <li
                        key={tip}
                        className="flex gap-2 text-sm text-slate"
                      >
                        <span className="mt-0.5 text-red">&#8226;</span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Content calendar */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Content calendar for agents
            <span className="text-red">.</span>
          </h2>
          <p className="mt-2 text-sm text-slate">
            A practical content mix to keep your social feeds active and
            varied.
          </p>

          <div className="mt-6 overflow-x-auto rounded-xl border border-stone">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone bg-cream/50">
                  <th className="px-4 py-3 text-left font-medium text-charcoal">
                    Content type
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-charcoal">
                    Frequency
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-charcoal">
                    Platform
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone">
                {CONTENT_IDEAS.map((row) => (
                  <tr key={row.type} className="hover:bg-cream/30">
                    <td className="px-4 py-3 font-medium text-charcoal">
                      {row.type}
                    </td>
                    <td className="px-4 py-3 text-slate">
                      {row.frequency}
                    </td>
                    <td className="px-4 py-3 text-slate">
                      {row.platform}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Best practices */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Six best practices
            <span className="text-red">.</span>
          </h2>
          <div className="mt-6 space-y-4">
            {BEST_PRACTICES.map((item) => (
              <div
                key={item.title}
                className="rounded-xl border border-stone bg-white p-5"
              >
                <h3 className="text-sm font-semibold text-charcoal">
                  {item.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-slate">
                  {item.detail}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            { stat: "6x", label: "more engagement with native video on Facebook" },
            { stat: "73%", label: "of vendors prefer agents who use video" },
            { stat: "2x", label: "more enquiries on listings with video" },
            { stat: "<60s", label: "to create a property video with QuiteRight" },
          ].map((item) => (
            <div
              key={item.stat}
              className="bg-charcoal px-5 py-6 text-center"
            >
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">
                {item.label}
              </p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related resources<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {[
              {
                href: "/guides/property-video-marketing",
                title: "Property video marketing",
                desc: "The definitive guide to video types, platforms, and results.",
              },
              {
                href: "/guides/property-photography-tips",
                title: "Property photography tips",
                desc: "Better photos mean better videos and social content.",
              },
              {
                href: "/guides/winning-instructions",
                title: "Winning instructions",
                desc: "How social media and video help you win at market appraisals.",
              },
              {
                href: "/examples",
                title: "Video examples",
                desc: "See property videos created for social media.",
              },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Explore
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-3.5 transition-transform group-hover:translate-x-0.5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            The video content that fuels social media
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Social media runs on video. QuiteRight creates
            scroll-stopping property videos from your listing photos in
            under 60 seconds, ready for Instagram, TikTok, Facebook,
            and YouTube.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
          <p className="mt-5 text-sm text-warm-grey">
            14-day free trial &middot; No credit card &middot; Cancel any time
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}
